import generatePDF, { inchesToPDFUnits, PDFObjectType, measureText, PDFObject } from "./pdf-generation";


export interface SimpleLabelGenerationOptions {
  widthInches: number;
  heightInches: number;

  text: string; // if multi-line, must be pre-wrapped (contain the newlines where you want them)
  lineHeightMultiplier?: number; // default 1.5
  bold?: boolean; // default true
  textColor?: [number, number, number]; // rgb normalized color, 0..1, default black

  borderColor?: [number, number, number]; // rgb normalized color, 0..1, default black. null this to omit the border.

  copies?: number; // defaults to 1 copy (one label on one pdf page)
}

export function base64SimpleLabelPDF(options: SimpleLabelGenerationOptions): string {
  let width = inchesToPDFUnits(options.widthInches);
  let height = inchesToPDFUnits(options.heightInches);

  // like many other label kinds, we want the horizontal dimension to be the 
  // dimension we lay the text out in.
  if (width < height) {
    const t = width;
    width = height;
    height = t;
  }

  const objects: PDFObject[] = [];

  let borderWidth = 0;
  const borderColor: [number, number, number] = options.borderColor === undefined ? [0, 0, 0] : options.borderColor;
  if (borderColor) {
    // at least 12 units wide, at most 36, otherwise scaled up depending on available room.
    borderWidth = Math.min(36, Math.max(12, Math.min(width, height) / 10));
    objects.push({
      type: PDFObjectType.rect,
      left: 0,
      bottom: 0,
      width,
      height,
      color: [1, 1, 1],
      strokeColor: borderColor,
      strokeWidth: borderWidth,
    });
  }

  // @TODO we could do our own line-wrapping. but right now we require multi-line text
  // to be pre-wrapped/contain the newline characters.
  const lines = options.text.split("\n").map(l => l.trim()).filter(l => !!l && l.length > 0);

  const lineWidths = [];
  const effectiveWidth = width - borderWidth;
  const lineHeightMultiplier = options.lineHeightMultiplier ?? 1.5;
  const isBold = options.bold === undefined ? true : options.bold;
  let fontSize = effectiveWidth/10;
  let largestLineWidth = 0;
  let largestLineHeight = 0;
  let largestDescent = 0;
  let textHeight = 0;
  for (const line of lines) {
    let textMetrics: TextMetrics;
    let textWidth = 0;
    do {
      textMetrics = measureText(line, "helvetica", `${fontSize}pt`, isBold); // we use 'pt' because it's easier to be 1-to-1 with PDF units this way.
      textWidth = textMetrics.width * 0.75;
      if (textWidth > effectiveWidth) {
        fontSize--; // this line overflows the available space, shrink the font size and try again.
        continue;
      }
      // the text fits. measure the descent of this font-size - because PDF seems to
      // render text starting at the 'baseline', instead of the lower bounding y-value,
      // we'll need to know the descent of the font to align it vertically correctly
      largestDescent = Math.max(largestDescent, textMetrics.actualBoundingBoxDescent*0.75);
      break;
    } while (true);

    lineWidths.push(textWidth);
    largestLineWidth = Math.max(largestLineWidth, textWidth);

    const height = (textMetrics.actualBoundingBoxAscent*0.75 + textMetrics.actualBoundingBoxDescent*0.75) * lineHeightMultiplier;
    largestLineHeight = Math.max(largestLineHeight, height);
    textHeight += height;
  }

  const halfWidth = width/2;
  const halfHeight = height/2;
  const halfTextHeight = textHeight/2;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineWidth = lineWidths[i];
    objects.push({
      type: PDFObjectType.text,
      left: halfWidth - largestLineWidth/2 + (largestLineWidth - lineWidth)/2,
      bottom: halfHeight + halfTextHeight - (i+1)*largestLineHeight + largestDescent,
      text: line,
      color: options.textColor ?? [0, 0, 0],
      bold: true,
      fontSize,
    });
  }

  const pages = [];
  const copies = options.copies ?? 1;
  for (let i = 0; i < copies; i++) {
    pages.push({ objects });
  }

  const pdf = generatePDF(width, height, pages);

  return window.btoa(pdf);
}
